import { useEffect, useState, useCallback } from "react";
import { Wand2, Plus, X, Loader2, Check, Globe, Star, Tag, Layers, Rocket } from "lucide-react";
import {
  saveBuildezeProfile, getBuildezeProfile, buildezeFeed,
  listSources, fetchCountries,
} from "../lib/api";
import { getStoredEmail } from "../lib/useNewsezeUser";
import NewsCard from "./NewsCard";
import BoostModal from "./BoostModal";

const TOPIC_OPTIONS = [
  { key: "government", label: "Politics" },
  { key: "economy", label: "Business" },
  { key: "markets", label: "Markets" },
  { key: "world", label: "World" },
  { key: "technology", label: "Tech" },
  { key: "health", label: "Health" },
  { key: "local", label: "Local" },
];

const FLAG = (cc) => {
  if (!cc || cc.length !== 2) return "🌐";
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

export default function BuildezeSection({ onOpenBrief, onBrief, onShare }) {
  const email = getStoredEmail();
  const [profile, setProfile] = useState({ topics: [], subjects: [], countries: [], favorites: [] });
  const [subjectInput, setSubjectInput] = useState("");
  const [sources, setSources] = useState([]);
  const [countries, setCountries] = useState([]);
  const [feed, setFeed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [boost, setBoost] = useState(null);  // null | { subject?: string }

  const loadFeed = useCallback(async () => {
    if (!email) return;
    setLoadingFeed(true);
    try { setFeed(await buildezeFeed(email)); } catch {} finally { setLoadingFeed(false); }
  }, [email]);

  useEffect(() => {
    let alive = true;
    listSources().then((s) => alive && setSources(s)).catch(() => {});
    fetchCountries().then((c) => alive && setCountries(c)).catch(() => {});
    if (email) {
      getBuildezeProfile(email).then((p) => {
        if (alive) setProfile({
          topics: p.topics || [], subjects: p.subjects || [],
          countries: p.countries || [], favorites: p.favorites || [],
        });
      }).catch(() => {});
      loadFeed();
    }
    return () => { alive = false; };
  }, [email, loadFeed]);

  const toggle = (key, value) => {
    setSaved(false);
    setProfile((p) => {
      const list = p[key] || [];
      const next = list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
      return { ...p, [key]: next.slice(0, 25) };
    });
  };

  const addSubject = (e) => {
    e?.preventDefault?.();
    const t = subjectInput.trim();
    if (!t) return;
    setSubjectInput("");
    setSaved(false);
    setProfile((p) => ({ ...p, subjects: Array.from(new Set([...(p.subjects || []), t])).slice(0, 25) }));
  };

  const removeSubject = (t) => {
    setSaved(false);
    setProfile((p) => ({ ...p, subjects: (p.subjects || []).filter((s) => s !== t) }));
  };

  const save = async () => {
    if (!email) return;
    setSaving(true);
    try {
      await saveBuildezeProfile({ email, ...profile });
      setSaved(true);
      await loadFeed();
      setTimeout(() => setSaved(false), 2200);
    } catch {} finally { setSaving(false); }
  };

  // === Anonymous (not subscribed yet) ===
  if (!email) {
    return (
      <section className="py-12 my-6 rounded-2xl bg-gradient-to-br from-[#0e2d63] to-[#1e40af] text-white -mx-5 px-5" id="buildeze" data-testid="buildeze-anon">
        <div className="max-w-[1000px] mx-auto">
          <div className="flex items-center gap-2 text-[var(--gold)] text-[11px] uppercase tracking-[0.18em] font-black">
            <Wand2 size={14} /> Buildeze · Build Your News
          </div>
          <h2 className="text-[36px] md:text-[44px] font-black tracking-[-0.03em] leading-[1.05] mt-3 max-w-[720px]">
            One feed. Built around you.
          </h2>
          <p className="text-[16px] opacity-85 mt-3 max-w-[640px] leading-[1.55]">
            Pick your topics, subjects, countries, and favorite channels. Newseze blends them into
            a single trust-ranked feed that updates every hour — and pays you to read it.
          </p>
          <button onClick={onOpenBrief} className="btn gold mt-5" data-testid="buildeze-anon-cta">
            Start Buildeze (free)
          </button>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7 text-[13px]">
            {[
              { i: Tag, t: "Subjects", d: "Tesla, AI, FOMC, vaccines — anything you care about." },
              { i: Layers, t: "Topics", d: "Politics, Markets, Health, Tech, Local." },
              { i: Globe, t: "Countries", d: "25+ countries · independent sources only." },
              { i: Star, t: "Favorite Channels", d: "Your trusted newsrooms, prioritized first." },
            ].map((x) => (
              <div key={x.t} className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/15">
                <x.i size={16} className="text-[var(--gold)]" />
                <div className="font-black mt-2">{x.t}</div>
                <div className="opacity-80 text-[12px] mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // === Subscriber experience ===
  const domestic = sources.filter((s) => !s.international);
  const intl = sources.filter((s) => s.international);

  return (
    <section className="py-10" id="buildeze" data-testid="buildeze-section">
      <div className="section-title">
        <span className="pip" />
        <h2><Wand2 size={18} className="inline mr-2 -mt-1" />Buildeze · Build Your News</h2>
        <button onClick={save} disabled={saving} className="ml-auto btn primary" data-testid="buildeze-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <><Check size={14} /> Saved · Feed refreshed</> : "Save & build my feed"}
        </button>
      </div>

      <div className="rounded-xl border border-[var(--line)] bg-white p-5">
        {/* Topics */}
        <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[var(--muted)] mb-2 flex items-center gap-2"><Layers size={12} /> Topics</div>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {TOPIC_OPTIONS.map((t) => (
            <button
              key={t.key}
              onClick={() => toggle("topics", t.key)}
              className={`chip ${profile.topics.includes(t.key) ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
              data-testid={`buildeze-topic-${t.key}`}
            >{t.label}</button>
          ))}
        </div>

        {/* Subjects */}
        <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[var(--muted)] mb-2 flex items-center gap-2"><Tag size={12} /> Subjects · keywords</div>
        <form onSubmit={addSubject} className="flex gap-2 mb-2">
          <input
            className="field flex-1"
            placeholder="e.g. AI regulation, Tesla, climate, FOMC"
            value={subjectInput}
            onChange={(e) => setSubjectInput(e.target.value)}
            data-testid="buildeze-subject-input"
          />
          <button type="submit" className="btn"><Plus size={14} /> Add</button>
        </form>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {(profile.subjects || []).map((s) => (
            <span key={s} className="chip !bg-[var(--gold)] !text-[#1d1303] !border-[var(--gold)] flex items-center gap-1" data-testid={`buildeze-subject-${s}`}>
              {s}
              <button
                onClick={() => setBoost({ subject: s })}
                title="Boost this subject — $9 / 30 days"
                className="hover:text-[var(--blue)]"
                aria-label={`boost ${s}`}
                data-testid={`buildeze-boost-${s}`}
              >
                <Rocket size={11} />
              </button>
              <button onClick={() => removeSubject(s)} className="hover:text-[var(--red)]" aria-label="remove"><X size={11} /></button>
            </span>
          ))}
          {(profile.subjects || []).length === 0 && (
            <span className="text-[12px] text-[var(--muted)] italic">No subjects yet. Type anything you care about and hit Add.</span>
          )}
        </div>

        {/* Countries */}
        <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[var(--muted)] mb-2 flex items-center gap-2"><Globe size={12} /> Countries · independent sources only</div>
        <div className="flex flex-wrap gap-1.5 mb-5">
          <button
            onClick={() => setProfile((p) => ({ ...p, countries: profile.countries.includes("US") ? profile.countries.filter((c) => c !== "US") : [...profile.countries, "US"] }))}
            className={`chip ${profile.countries.includes("US") ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
            data-testid="buildeze-country-US"
          >{FLAG("US")} United States</button>
          {countries.map((c) => (
            <button
              key={c.code}
              onClick={() => toggle("countries", c.code)}
              className={`chip ${profile.countries.includes(c.code) ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
              data-testid={`buildeze-country-${c.code}`}
            >
              {FLAG(c.code)} {c.name}
            </button>
          ))}
        </div>

        {/* Favorites */}
        <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[var(--muted)] mb-2 flex items-center gap-2"><Star size={12} /> Favorite Channels</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {domestic.map((s) => (
            <button key={s.name} onClick={() => toggle("favorites", s.name)}
              className={`chip ${profile.favorites.includes(s.name) ? "!bg-[var(--gold)] !text-[#1d1303] !border-[var(--gold)]" : ""}`}
              data-testid={`buildeze-fav-${s.name.replace(/\s+/g, "-").toLowerCase()}`}>
              {profile.favorites.includes(s.name) ? "★" : "☆"} {s.name}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {intl.map((s) => (
            <button key={s.name} onClick={() => toggle("favorites", s.name)}
              className={`chip ${profile.favorites.includes(s.name) ? "!bg-[var(--gold)] !text-[#1d1303] !border-[var(--gold)]" : ""}`}
              data-testid={`buildeze-fav-${s.name.replace(/\s+/g, "-").toLowerCase()}`}>
              {profile.favorites.includes(s.name) ? "★" : "☆"} {FLAG(s.country)} {s.name}
            </button>
          ))}
        </div>
      </div>

      {/* My personalized feed */}
      <div className="mt-8" data-testid="buildeze-feed">
        <div className="section-title">
          <span className="pip" />
          <h2>My Newseze · personalized</h2>
          <button
            onClick={() => setBoost({ subject: (profile.subjects || [])[0] || "" })}
            className="ml-auto btn gold"
            title="Boost a subject — $9 / 30 days priority + 2× credits"
            data-testid="buildeze-boost-cta"
          >
            <Rocket size={14} /> Boost a subject · $9
          </button>
        </div>
        {loadingFeed ? (
          <div className="text-[14px] text-[var(--muted)] italic py-6 text-center">Building your feed…</div>
        ) : feed.length === 0 ? (
          <div className="text-[14px] text-[var(--muted)] italic py-6 text-center">No stories match yet. Try widening your topics or subjects.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {feed.slice(0, 8).map((a) => (
              <NewsCard key={a.id} article={a} onBrief={onBrief} onShare={onShare} />
            ))}
          </div>
        )}
      </div>

      {boost && (
        <BoostModal
          defaultSubject={boost.subject || ""}
          onClose={() => setBoost(null)}
          onNeedSubscribe={onOpenBrief}
        />
      )}
    </section>
  );
}
